import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { graphql, useSubscription } from 'relay-hooks'
import { useRouter } from 'react-router5'
import Consts from 'consts'
// eslint-disable-next-line no-restricted-imports -- use datadog and rollbar directly
import logging from 'shared/utils/logging'
import styled from '@emotion/styled'
import UnstyledButton from 'design-system/components/Button/UnstyledButton'
import fontWeights from 'design-system/tokens/fontWeights'
import lineHeights from 'design-system/tokens/lineHeights'
import location from 'shared/utils/location'
import relayService from 'shared/utils/relay'
import workerTimers from 'web-client/utils/workerTimers'
import datadog from 'shared/utils/logging/integrations/datadog'

import {
  sendCustomNotification,
  sendNotification,
} from 'design-system/components/Notifications/notificationUtils'
import useCurrentUser from 'web-client/hooks/useCurrentUser'
import useEnvironmentVariables from 'web-client/hooks/useEnvironmentVariables'
import Storage from 'web-client/utils/storage'
import type {
  useSwoopVersionUpdatedSubscription,
  useSwoopVersionUpdatedSubscription$data,
} from './__generated__/useSwoopVersionUpdatedSubscription.graphql'

export const CCAAS_REFRESH_BANNER_STORAGE_KEY = 'ccaas_version_refresh_pending'

// Used until the environment query resolves; both also have a default on
// FrontEndConfiguration, which is the real source of truth.
const DEFAULT_FORCE_REFRESH_COUNTDOWN = 360
const DEFAULT_FORCE_REFRESH_RANDOM_WAIT_TIME = 60

type PendingRefreshRecord = {
  timestamp: number
  previousVersion: string
}

type UseSwoopVersionState = {
  refreshRequired: boolean
  version: string
  serverVersion: string
  refreshRequiredIfNecessary: () => void
}

const SwoopVersionContext = createContext<UseSwoopVersionState>({
  refreshRequired: false,
  version: window.VERSION,
  serverVersion: window.VERSION,
  refreshRequiredIfNecessary: () => {},
})

const subscription = graphql`
  subscription useSwoopVersionUpdatedSubscription {
    versionUpdated {
      version
      urgent
    }
  }
`

const LinkButton = styled(UnstyledButton)`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
  display: inline-block;
  font-weight: ${fontWeights.bold};
`

const NotificationWrapper = styled.span`
  text-align: left;
  line-height: ${lineHeights.bodySmall};
`

export const ForceRefreshNotification = () => {
  // subscribe: false — the countdown is only read as the initial state, so a later
  // environmentUpdated push could not change it and would only force a re-render.
  const { forceRefreshCountdown } = useEnvironmentVariables({ subscribe: false })
  const [time, setTime] = useState(forceRefreshCountdown ?? DEFAULT_FORCE_REFRESH_COUNTDOWN)

  useEffect(() => {
    workerTimers.setInterval(() => {
      setTime((prev) => (prev > 0 ? prev - 1 : prev))
    }, 1000)
  }, [])

  useEffect(() => {
    if (time <= 0) {
      location.reload()
    }
  })
  return (
    <NotificationWrapper className="message">
      You have <em>{time} seconds</em> to save your work and refresh the page. This is required to
      process a critical application update.
      <LinkButton onClick={() => location.reload()} type={'button'}>
        Refresh Now
      </LinkButton>
    </NotificationWrapper>
  )
}

export const SwoopVersionProvider = ({ children }: { readonly children?: ReactNode }) => {
  const [state, setState] = useState<Omit<UseSwoopVersionState, 'refreshRequiredIfNecessary'>>({
    refreshRequired: false,
    serverVersion: window.VERSION,
    version: window.VERSION,
  })
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const notifiedVersionsRef = useRef<string[]>([])
  const isCcaasEnabledRef = useRef(false)

  // Held in a ref so `checkSoftVersionUpdate` stays stable: it feeds the `onVersionReceived`
  // effect below, which would otherwise re-subscribe whenever `currentUser` changes identity.
  useEffect(() => {
    isCcaasEnabledRef.current = !!currentUser?.permissions.isCommunicationsIntegrationEnabled
  }, [currentUser])

  // `onVersionUpdated` below is deliberately bound once, so the wait time is read
  // through a ref rather than captured — that way a live settings change still
  // applies to the next version broadcast.
  const { forceRefreshRandomWaitTime } = useEnvironmentVariables()
  const forceRefreshRandomWaitTimeRef = useRef(forceRefreshRandomWaitTime)
  forceRefreshRandomWaitTimeRef.current = forceRefreshRandomWaitTime

  // Checks if a CCaaS user refreshed to a new version by checking the stored refresh data.
  useEffect(() => {
    let serializedRecord: string | null | undefined
    try {
      serializedRecord = Storage.get(CCAAS_REFRESH_BANNER_STORAGE_KEY, { skipCache: true })
      if (!serializedRecord) {
        return
      }

      const record = JSON.parse(serializedRecord) as PendingRefreshRecord
      if (window.VERSION !== record.previousVersion) {
        const elapsedMs = Date.now() - record.timestamp
        datadog.logInfo('CCaaS user refreshed to latest Swoop version', {
          elapsedMs,
          previousVersion: record.previousVersion,
          newVersion: window.VERSION,
          topic: 'CCaaS',
          component: 'Swoop Version Change',
        })
        Storage.remove(CCAAS_REFRESH_BANNER_STORAGE_KEY)
      }
    } catch (error) {
      datadog.logWarning('Failed to process CCaaS refresh banner record', {
        error: error instanceof Error ? error.message : String(error),
        serializedRecord,
        topic: 'CCaaS',
        component: 'Swoop Version Change',
      })
      Storage.remove(CCAAS_REFRESH_BANNER_STORAGE_KEY)
    }
  }, [])

  // Takes the version as an argument so it can run in the same tick the version arrives,
  // before the corresponding `setState` has been applied.
  const handleVersionChangeForCCaaS = useCallback((announcedVersion: string) => {
    if (notifiedVersionsRef.current.includes(announcedVersion)) {
      return
    }

    datadog.logInfo('CCaaS enabled user notified of new Swoop version', {
      newVersion: announcedVersion,
      topic: 'CCaaS',
      component: 'Swoop Version Change',
      notifiedVersionChanges: [...notifiedVersionsRef.current, announcedVersion],
    })
    sendNotification(
      'Please save your work and refresh to view the latest version of Swoop.',
      Consts.NOTIFICATION_TYPES.WARNING,
      { autoClose: false }
    )
    notifiedVersionsRef.current = [...notifiedVersionsRef.current, announcedVersion]
    Storage.set(
      CCAAS_REFRESH_BANNER_STORAGE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        previousVersion: window.VERSION,
      } satisfies PendingRefreshRecord)
    )
  }, [])

  const refreshRequiredIfNecessary = useCallback(() => {
    if (state.refreshRequired) {
      // Temporary solution to avoid silent refresh for CCaaS enabled users
      if (currentUser?.permissions.isCommunicationsIntegrationEnabled) {
        handleVersionChangeForCCaaS(state.serverVersion)
      } else {
        location.reload()
      }
    }
  }, [state.refreshRequired, state.serverVersion, currentUser, handleVersionChangeForCCaaS])

  const checkSoftVersionUpdate = useCallback(
    (version: string, timeout = 0) => {
      if (version !== window.VERSION) {
        logging.logInfo('Soft refresh required due to new version')
        workerTimers.setTimeout(() => {
          // Forcing a document load would tear down the Amazon Connect WebRTC session and
          // drop any live call, so CCaaS agents are asked to refresh when it suits them.
          if (isCcaasEnabledRef.current) {
            handleVersionChangeForCCaaS(version)
            setState({
              refreshRequired: true,
              serverVersion: version,
              version: window.VERSION,
            })
            return
          }

          if (router) {
            // On navigation force a refresh. The role is re-checked here because this
            // middleware can be installed before `currentUser` resolves and then stays
            // installed for the life of the page, long enough for a call to be connected.
            router.useMiddleware(
              () => (toState: { path: string }, fromState: { path: string }, done: () => void) => {
                if (
                  !isCcaasEnabledRef.current &&
                  fromState?.path &&
                  fromState.path !== toState.path
                ) {
                  window.location.href = `${toState.path}`
                  return false
                }
                done()
              }
            )
            setState({
              refreshRequired: true,
              serverVersion: version,
              version: window.VERSION,
            })
          } else {
            logging.logError('Soft refresh required but no router found')
          }
        }, timeout)
      }
    },
    [router, handleVersionChangeForCCaaS]
  )

  useEffect(() => {
    return relayService.onVersionReceived((version: string) => {
      checkSoftVersionUpdate(version, 1000 * 60 * 10)
    })
  }, [checkSoftVersionUpdate])

  const onVersionUpdated = useCallback((data?: useSwoopVersionUpdatedSubscription$data | null) => {
    if (!data) {
      return
    }
    const { version, urgent } = data.versionUpdated
    const refreshRequired = version !== window.VERSION
    checkSoftVersionUpdate(version)

    // Set the router state so other downstream components can also refresh
    setState({
      refreshRequired,
      serverVersion: version,
      version: window.VERSION,
    })

    // When it's urgent, show countdown
    if (urgent && refreshRequired) {
      workerTimers.setTimeout(
        () => {
          sendCustomNotification(<ForceRefreshNotification />, Consts.NOTIFICATION_TYPES.ERROR)
        },
        Math.random() *
          (forceRefreshRandomWaitTimeRef.current ?? DEFAULT_FORCE_REFRESH_RANDOM_WAIT_TIME) *
          1000
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot stable handler bound once on mount; checkSoftVersionUpdate is captured by closure and its identity changes do not affect the desired behavior of `onVersionUpdated`
  }, [])

  useSubscription<useSwoopVersionUpdatedSubscription>(
    useMemo(
      () => ({
        subscription,
        onNext: onVersionUpdated,
        variables: {},
      }),
      [onVersionUpdated]
    )
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      refreshRequiredIfNecessary,
    }),
    [state, refreshRequiredIfNecessary]
  )

  return (
    <SwoopVersionContext.Provider value={contextValue}>{children}</SwoopVersionContext.Provider>
  )
}

const useSwoopVersion = () => {
  const context = useContext(SwoopVersionContext)
  if (context === undefined) {
    throw new Error('useSwoopVersion must be used within a SwoopVersionProvider')
  }
  return context
}

export default useSwoopVersion
